import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { CSVIcon, CloseIcon } from "../../assets/Icons";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import { useSelector } from "react-redux";
import axios from "axios";
import { useParams } from "react-router-dom";

const REQUIRED_FIELDS = ["Site Name", "OBR Org", "OBR BU","Region", "Country"];

const SiteCSV = ({ CSVModal, setCSVModal, onSubmit = () => {} }) => {
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const { uuid: mainSiteId } = useParams();

  const onDrop = (acceptedFiles) => {
    setUploadedFile(acceptedFiles[0]);
    setError("");
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: ".csv",
  });

  const uploadSiteListCSV = async (sites) => {
    try {
      await axios.post(
        `${configData.config.API_URL}/api/site/subsite/create/bulk/${mainSiteId}`,
        { sites },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (err) {
      console.error("Error saving sites:", err);
      throw err;
    }
  };

  const handleUpload = async () => {
    if (!uploadedFile) {
      setError("Please select a CSV file.");
      return;
    }

    setProcessing(true);

    try {
      const result = await new Promise((resolve, reject) => {
        Papa.parse(uploadedFile, {
          header: true,
          skipEmptyLines: true,
          complete: (res) => resolve(res),
          error: (err) => reject(err),
        });
      });

      const { data } = result;

      // Validate required headers
      const missingFields = REQUIRED_FIELDS.filter((field) => !Object.keys(data[0] || {}).includes(field));

      if (missingFields.length) {
        setError(`Missing fields: ${missingFields.join(", ")}`);
        setProcessing(false);
        return;
      }

      const siteList = data.map((row) => ({
        siteName: row["Site Name"]?.trim(),
        obrOrg: row["OBR Org"]?.trim(),
        obrBu: row["OBR BU"]?.trim(),
        region: row["Region"]?.trim() || "",
        country: row["Country"]?.trim(),
      }));

      await uploadSiteListCSV(siteList);

      setUploadedFile(null);
      setError("");
      setCSVModal(false);
      onSubmit();
    } catch (err) {
      console.error("CSV upload failed:", err);
      setError("Error uploading site list. Please check your CSV.");
    }

    setProcessing(false);
  };

  const handleDownloadTemplate = () => {
    const csvData = [REQUIRED_FIELDS, ["Site X", "Org A", "BU 1", "Asia", "India"]];
    const csvContent = csvData.map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `site-list-sample.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  useEffect(() => {
    return () => {
      setError("");
      setUploadedFile(null);
      setProcessing(false);
    };
  }, []);

  return (
    <Modal show={CSVModal} size="lg" centered onHide={() => setCSVModal(false)}>
      <Modal.Header className="modalheader">
        Upload Site List CSV
        <span onClick={() => setCSVModal(false)}>
          <CloseIcon />
        </span>
      </Modal.Header>

      <Modal.Body className="my-2">
        <div className="row px-3">
          <div {...getRootProps()} className="dropzone-csv">
            <input {...getInputProps()} />
            <span>
              <CSVIcon />
              {uploadedFile ? (
                <div className="text-center">
                  <p>
                    {uploadedFile.name} <span className="text-success">uploaded!</span>
                  </p>
                  <p>Click 'Save' to continue or upload another file.</p>
                </div>
              ) : (
                <p>Drag & drop a CSV file here, or click to select one</p>
              )}
            </span>
          </div>

          <div className="col-12 csv-info mt-3">
            <p>
              Required Fields<span>*</span>
            </p>
            <p>{REQUIRED_FIELDS.join(", ")}</p>
            {error && <p className="text-danger">{error}</p>}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="footerbtn csv-modal">
        <p onClick={handleDownloadTemplate} className="template-down m-0">
          Download Site CSV Template
        </p>

        <div className="d-flex">
          <button
            className="cancel"
            onClick={() => {
              setCSVModal(false);
              setUploadedFile(null);
              setError("");
            }}
          >
            Cancel
          </button>

          {processing ? (
            <button className="loading save">
              <svg viewBox="25 25 50 50">
                <circle r="20" cy="50" cx="50"></circle>
              </svg>
            </button>
          ) : (
            <button className="save" disabled={!uploadedFile} onClick={handleUpload}>
              Save
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SiteCSV;
